import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronLeft, BookMarked, Download, Package, Loader2, Lock,
  Sparkles, Check, Truck, RefreshCw, CircleCheck, CircleAlert, Eye,
} from "lucide-react";
import UserBadge from "@/components/UserBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  fetchKeepsakeManuscript,
  fetchKeepsakeProducts,
  fetchKeepsakeEstimate,
  createKeepsakeCheckout,
  fetchKeepsakeOrders,
  fetchKeepsakeOrder,
} from "@/lib/api";
import { API } from "@/lib/api";
import { exportKeepsakeBook } from "@/lib/pdf";

// Original binding fee for the strike-through display when Attending tier
// gets it at cost. Kept in sync with KEEPSAKE_MARKUP_USD backend default.
const MARKUP_USD_LABEL = "9.00";
import { toast } from "sonner";

// Countries where Lulu currently prints/fulfills; keep the list short
// so users don't tab through 250 options on mobile.
const COUNTRIES = [
  { code: "US", label: "United States" },
  { code: "CA", label: "Canada" },
  { code: "GB", label: "United Kingdom" },
  { code: "AU", label: "Australia" },
  { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },
  { code: "IE", label: "Ireland" },
  { code: "NL", label: "Netherlands" },
  { code: "ES", label: "Spain" },
  { code: "IT", label: "Italy" },
  { code: "SE", label: "Sweden" },
  { code: "NO", label: "Norway" },
  { code: "DK", label: "Denmark" },
  { code: "FI", label: "Finland" },
  { code: "NZ", label: "New Zealand" },
];

const INITIAL_ADDRESS = {
  name: "",
  street1: "",
  street2: "",
  city: "",
  state_code: "",
  postcode: "",
  country_code: "US",
  phone_number: "",
};

const REQUIRED_FIELDS = ["name", "street1", "city", "state_code", "postcode", "country_code", "phone_number"];

const statusChip = (status) => {
  if (!status) return { label: "Awaiting payment", tone: "muted" };
  const map = {
    CREATED: { label: "Received", tone: "muted" },
    UNPAID: { label: "Payment pending", tone: "muted" },
    PAYMENT_IN_PROGRESS: { label: "Payment processing", tone: "muted" },
    PRODUCTION_DELAYED: { label: "Queued for press", tone: "warn" },
    PRODUCTION_READY: { label: "Queued for press", tone: "warn" },
    IN_PRODUCTION: { label: "At the press", tone: "warn" },
    SHIPPED: { label: "Shipped", tone: "ok" },
    DELIVERED: { label: "Delivered", tone: "ok" },
    REJECTED: { label: "Rejected — contact support", tone: "err" },
    CANCELED: { label: "Canceled", tone: "err" },
    SUBMIT_ERROR: { label: "Submission error — reach out", tone: "err" },
  };
  return map[status] || { label: status.replace(/_/g, " ").toLowerCase(), tone: "muted" };
};

export default function Keepsake() {
  const location = useLocation();
  const navigate = useNavigate();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const paidJustNow = query.get("paid") === "1";
  const cancelledJustNow = query.get("cancelled") === "1";
  const paidOrderId = query.get("order_id");

  const [manuscript, setManuscript] = useState(null);
  const [products, setProducts] = useState([]);
  const [lulReady, setLulReady] = useState(true);
  const [foilPrice, setFoilPrice] = useState(6);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [address, setAddress] = useState(INITIAL_ADDRESS);
  const [quantity, setQuantity] = useState(1);
  // NEW · scope + optional chapter + gift + upgrades
  const [scope, setScope] = useState("full");
  const [chapterId, setChapterId] = useState(null);
  const [finishUpgrade, setFinishUpgrade] = useState(null); // null | "foil"
  const [includeCompanion, setIncludeCompanion] = useState(false);
  const [isGift, setIsGift] = useState(false);
  const [gifterName, setGifterName] = useState("");
  const [inscription, setInscription] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateError, setEstimateError] = useState(null);
  const [ordering, setOrdering] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [m, p, o] = await Promise.all([
        fetchKeepsakeManuscript(),
        fetchKeepsakeProducts(),
        fetchKeepsakeOrders().catch(() => ({ orders: [] })),
      ]);
      setManuscript(m);
      setProducts(p.products || []);
      setLulReady(!!p.lulu_ready);
      setFoilPrice(p.foil_upgrade_usd || 6);
      setOrders(o.orders || []);
      if (!selectedProductId && p.products?.length) {
        const rec = p.products.find((x) => x.recommended) || p.products[0];
        setSelectedProductId(rec.id);
      }
    } catch (e) {
      console.error(e);
      toast.error("Could not load the bindery.");
    } finally {
      setLoading(false);
    }
  }, [selectedProductId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // If the user just came back from Stripe success, refresh order + confirm
  useEffect(() => {
    if (paidJustNow && paidOrderId) {
      toast.success("Payment received. Your book is heading to the press.");
      fetchKeepsakeOrder(paidOrderId).then(() => loadAll()).catch(() => {});
      const timeout = setTimeout(() => navigate("/keepsake", { replace: true }), 1200);
      return () => clearTimeout(timeout);
    }
    if (cancelledJustNow) {
      toast("The order was not placed.", { description: "Try again anytime." });
      navigate("/keepsake", { replace: true });
    }
  }, [paidJustNow, cancelledJustNow, paidOrderId, navigate, loadAll]);

  const canDownloadPreview = manuscript && manuscript.totals.entries > 0;
  const unlocked = manuscript?.unlocked;
  const canOrder = unlocked && lulReady;

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === selectedProductId),
    [products, selectedProductId],
  );

  const addressComplete = useMemo(
    () => REQUIRED_FIELDS.every((k) => (address[k] || "").trim().length > 0),
    [address],
  );

  // Debounced live cost estimate whenever address/product/qty changes.
  useEffect(() => {
    if (!canOrder || !selectedProductId || !addressComplete) {
      setEstimate(null);
      setEstimateError(null);
      return;
    }
    if (scope === "chapter" && !chapterId) {
      setEstimate(null);
      setEstimateError(null);
      return;
    }
    let cancelled = false;
    setEstimateLoading(true);
    setEstimateError(null);
    const t = setTimeout(async () => {
      try {
        const est = await fetchKeepsakeEstimate({
          product_id: selectedProductId,
          quantity,
          shipping_address: address,
          shipping_level: "MAIL",
          scope,
          section_id: scope === "chapter" ? chapterId : null,
          finish_upgrade: finishUpgrade,
          include_companion: includeCompanion,
        });
        if (!cancelled) setEstimate(est);
      } catch (e) {
        if (!cancelled) {
          const detail =
            typeof e?.response?.data?.detail === "object"
              ? (e.response.data.detail.error || "Could not estimate cost")
              : e?.response?.data?.detail || "Could not estimate cost";
          setEstimateError(detail);
          setEstimate(null);
        }
      } finally {
        if (!cancelled) setEstimateLoading(false);
      }
    }, 550);
    return () => { cancelled = true; clearTimeout(t); };
  }, [canOrder, selectedProductId, quantity, address, addressComplete, scope, chapterId, finishUpgrade, includeCompanion]);

  const handleDownloadPdfPreview = () => {
    if (!manuscript) return;
    exportKeepsakeBook({ manuscript, userName: manuscript.user_name });
    toast.success("The digital keepsake is downloading.");
  };

  const handleOrder = async () => {
    if (!estimate) {
      toast.error("Wait for the cost estimate before ordering.");
      return;
    }
    setOrdering(true);
    try {
      const r = await createKeepsakeCheckout({
        product_id: selectedProductId,
        quantity,
        shipping_address: address,
        shipping_level: "MAIL",
        origin_url: window.location.origin,
        scope,
        section_id: scope === "chapter" ? chapterId : null,
        finish_upgrade: finishUpgrade,
        include_companion: includeCompanion,
        is_gift: isGift,
        gifter_name: isGift ? (gifterName || null) : null,
        inscription: isGift ? (inscription || null) : null,
      });
      if (r?.checkout_url) {
        window.location.assign(r.checkout_url);
      } else {
        toast.error("Could not open Stripe checkout.");
      }
    } catch (e) {
      console.error(e);
      const detail =
        typeof e?.response?.data?.detail === "object"
          ? (e.response.data.detail.error || "Could not create the order.")
          : e?.response?.data?.detail || "Could not create the order.";
      toast.error(detail);
    } finally {
      setOrdering(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      <div className="max-w-5xl mx-auto px-5 sm:px-6 md:px-16 pt-10 sm:pt-14 md:pt-20 pb-32 relative z-10">
        <div className="flex items-center justify-between mb-8 sm:mb-12">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-200 transition-colors"
            data-testid="back-to-dashboard"
          >
            <ChevronLeft className="h-3 w-3" /> The Workbook
          </Link>
          <UserBadge />
        </div>

        <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-3">
          The Bound Edition
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-bone-100 leading-tight mb-4">
          Your year, in a book <em className="text-bone-300">you can hold.</em>
        </h1>
        <p className="font-serif italic text-base sm:text-lg text-bone-400 max-w-2xl leading-relaxed mb-10">
          Every prompt, every answer, every Mirror reflection — set in warm serif type,
          printed on demand by Lulu, and shipped to your door.
        </p>

        {loading ? (
          <div className="flex items-center gap-3 p-8 border border-obsidian-600">
            <Loader2 className="h-4 w-4 animate-spin text-terracotta" />
            <p className="font-sans text-bone-400">Gathering your manuscript…</p>
          </div>
        ) : (
          <>
            {/* Manuscript summary */}
            <div
              data-testid="keepsake-summary"
              className="border border-obsidian-600 bg-obsidian-800 p-5 sm:p-6 mb-8 grid grid-cols-3 gap-4"
            >
              <Metric label="Sections" value={manuscript.totals.sections_touched} />
              <Metric label="Entries" value={manuscript.totals.entries} />
              <Metric label="Mirror turns" value={manuscript.totals.conversation_turns} />
            </div>

            {!unlocked && (
              <div
                data-testid="keepsake-locked"
                className="border border-obsidian-600 bg-obsidian-800 p-5 sm:p-6 mb-8 flex items-start gap-3"
              >
                <Lock className="h-5 w-5 text-bone-500 mt-0.5" />
                <div>
                  <p className="font-serif text-lg text-bone-200">The bindery isn't open yet.</p>
                  <p className="font-sans text-sm text-bone-500 mt-1">
                    The bound edition unlocks after your first entry in the closing chapter,
                    or once you have saved 42+ entries.
                  </p>
                </div>
              </div>
            )}

            {/* Scope · full book vs single chapter */}
            {unlocked && (
              <section className="mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <BookMarked className="h-5 w-5 text-terracotta" />
                  <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-bone-500">What do you want to bind?</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <ScopeCard
                    active={scope === "full"}
                    label="The full journal"
                    tagline="Every chapter, every prompt, every Mirror turn. The complete keepsake."
                    onClick={() => setScope("full")}
                    testid="scope-full"
                  />
                  <ScopeCard
                    active={scope === "chapter"}
                    label="A single chapter"
                    tagline="A slim keepsake of one chapter — perfect for gifting or on-a-shelf."
                    onClick={() => setScope("chapter")}
                    testid="scope-chapter"
                  />
                </div>
                {scope === "chapter" && (
                  <div className="mt-3">
                    <label className="font-sans uppercase tracking-[0.2em] text-[0.55rem] text-bone-500">Chapter</label>
                    <select
                      value={chapterId || ""}
                      onChange={(e) => setChapterId(e.target.value || null)}
                      className="mt-1 w-full h-11 rounded-none bg-obsidian-900 border border-obsidian-600 text-bone-100 px-3 font-sans text-base sm:text-sm focus:outline-none focus:border-terracotta"
                      data-testid="scope-chapter-select"
                    >
                      <option value="">Choose a chapter…</option>
                      {(manuscript?.sections || []).map((s) => (
                        <option key={s.section_id} value={s.section_id}>
                          {s.title} · {s.entries.length} entr{s.entries.length === 1 ? "y" : "ies"}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </section>
            )}

            {/* Format picker */}
            {unlocked && (
              <section className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <BookMarked className="h-5 w-5 text-terracotta" />
                  <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-bone-500">Choose your binding</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {products.map((p) => {
                    const isSelected = selectedProductId === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setSelectedProductId(p.id)}
                        data-testid={`keepsake-product-${p.id}`}
                        className={`text-left p-4 sm:p-5 border transition-colors min-h-[112px]
                          ${isSelected
                            ? "border-terracotta bg-obsidian-800"
                            : "border-obsidian-600 bg-obsidian-900 hover:border-terracotta/60"}`}
                      >
                        <div className="flex items-start justify-between gap-3 mb-1">
                          <p className="font-serif text-bone-100 text-lg leading-tight">{p.label}</p>
                          {isSelected && <Check className="h-4 w-4 text-terracotta flex-shrink-0 mt-1" />}
                        </div>
                        <p className="font-sans text-xs text-bone-500 mb-2 leading-relaxed">{p.tagline}</p>
                        <div className="flex flex-wrap items-center gap-2">
                          {p.recommended && (
                            <span className="font-sans uppercase tracking-[0.18em] text-[0.55rem] text-terracotta">
                              Recommended
                            </span>
                          )}
                          <span className="font-sans uppercase tracking-[0.18em] text-[0.55rem] text-bone-500">
                            6″ × 9″
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Upgrades & Companion */}
            {unlocked && (
              <section className="mb-6 border border-obsidian-600 bg-obsidian-800 p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="h-5 w-5 text-terracotta" />
                  <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-bone-500">Details</p>
                </div>
                <div className="space-y-3">
                  <ToggleRow
                    label="Gold foil title imprint"
                    hint={`Warm gold foil on the front cover · +$${foilPrice.toFixed(2)}/copy`}
                    active={finishUpgrade === "foil"}
                    onChange={(v) => setFinishUpgrade(v ? "foil" : null)}
                    testid="upgrade-foil"
                  />
                  <ToggleRow
                    label="Include the Umbra companion ebook"
                    hint="A short reflection from Umbra Press placed before each chapter · no upcharge"
                    active={includeCompanion}
                    onChange={setIncludeCompanion}
                    testid="upgrade-companion"
                  />
                  <ToggleRow
                    label="Ship as a gift"
                    hint="Adds an inscription page after the title, and can ship straight to a friend"
                    active={isGift}
                    onChange={setIsGift}
                    testid="upgrade-gift"
                  />
                  {isGift && (
                    <div className="mt-3 pl-4 border-l border-terracotta/40 space-y-3">
                      <div>
                        <label className="font-sans uppercase tracking-[0.2em] text-[0.55rem] text-bone-500">Your name (appears under the inscription)</label>
                        <Input
                          value={gifterName}
                          onChange={(e) => setGifterName(e.target.value)}
                          placeholder="With love, Ada"
                          data-testid="gift-gifter-name"
                          className="mt-1 rounded-none bg-obsidian-900 border-obsidian-600 text-bone-100"
                        />
                      </div>
                      <div>
                        <label className="font-sans uppercase tracking-[0.2em] text-[0.55rem] text-bone-500">Inscription (up to 600 characters)</label>
                        <Textarea
                          value={inscription}
                          onChange={(e) => setInscription(e.target.value.slice(0, 600))}
                          rows={4}
                          placeholder="For the year we listened together —"
                          data-testid="gift-inscription"
                          className="mt-1 rounded-none bg-obsidian-900 border-obsidian-600 text-bone-100 font-serif italic"
                        />
                        <p className="mt-1 font-sans text-[0.6rem] text-bone-500">{inscription.length} / 600</p>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Book preview iframe (branded frame around Mozilla's PDF viewer) */}
            {unlocked && (
              <section className="mb-10">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Eye className="h-5 w-5 text-terracotta" />
                    <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-bone-500">Preview the first pages</p>
                  </div>
                  <button
                    onClick={() => setShowPreview((v) => !v)}
                    data-testid="keepsake-preview-toggle"
                    className="font-sans uppercase tracking-[0.18em] text-[0.65rem] text-bone-400 hover:text-terracotta"
                  >
                    {showPreview ? "Hide preview" : "Show preview"}
                  </button>
                </div>
                {showPreview && (
                  <div
                    className="border border-terracotta/60 bg-obsidian-800"
                    data-testid="keepsake-preview-frame"
                    style={{ padding: 6 }}
                  >
                    <div className="px-4 py-3 border-b border-obsidian-600 flex items-center justify-between">
                      <p className="font-serif italic text-bone-200 text-sm">
                        Your keepsake · a private preview of the first pages
                      </p>
                      <span className="font-sans uppercase tracking-[0.18em] text-[0.55rem] text-terracotta">
                        UMBRA PRESS
                      </span>
                    </div>
                    <iframe
                      title="keepsake-preview"
                      src={`${API}/keepsake/preview.pdf?scope=${encodeURIComponent(scope)}${scope === "chapter" && chapterId ? `&section_id=${encodeURIComponent(chapterId)}` : ""}&include_companion=${includeCompanion}#toolbar=0&navpanes=0&scrollbar=1`}
                      className="w-full bg-obsidian-900"
                      style={{ height: "70vh", minHeight: 480, border: 0 }}
                    />
                  </div>
                )}
              </section>
            )}


            {/* Shipping form */}
            {unlocked && (
              <section className="mb-10 border border-obsidian-600 bg-obsidian-800 p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Truck className="h-5 w-5 text-terracotta" />
                  <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-bone-500">Where should the book arrive?</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Full name *" testid="ship-name">
                    <Input value={address.name} onChange={(e) => setAddress({ ...address, name: e.target.value })}
                           className="rounded-none bg-obsidian-900 border-obsidian-600 text-bone-100" />
                  </Field>
                  <Field label="Phone *" testid="ship-phone">
                    <Input value={address.phone_number} onChange={(e) => setAddress({ ...address, phone_number: e.target.value })}
                           placeholder="+1 555 555 5555"
                           className="rounded-none bg-obsidian-900 border-obsidian-600 text-bone-100" />
                  </Field>
                  <Field label="Street *" testid="ship-street1" full>
                    <Input value={address.street1} onChange={(e) => setAddress({ ...address, street1: e.target.value })}
                           className="rounded-none bg-obsidian-900 border-obsidian-600 text-bone-100" />
                  </Field>
                  <Field label="Apt · suite (optional)" testid="ship-street2" full>
                    <Input value={address.street2} onChange={(e) => setAddress({ ...address, street2: e.target.value })}
                           className="rounded-none bg-obsidian-900 border-obsidian-600 text-bone-100" />
                  </Field>
                  <Field label="City *" testid="ship-city">
                    <Input value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })}
                           className="rounded-none bg-obsidian-900 border-obsidian-600 text-bone-100" />
                  </Field>
                  <Field label="State / Province code *" testid="ship-state">
                    <Input value={address.state_code} onChange={(e) => setAddress({ ...address, state_code: e.target.value })}
                           placeholder="e.g. NC"
                           className="rounded-none bg-obsidian-900 border-obsidian-600 text-bone-100" />
                  </Field>
                  <Field label="Postcode *" testid="ship-postcode">
                    <Input value={address.postcode} onChange={(e) => setAddress({ ...address, postcode: e.target.value })}
                           className="rounded-none bg-obsidian-900 border-obsidian-600 text-bone-100" />
                  </Field>
                  <Field label="Country *" testid="ship-country">
                    <select
                      value={address.country_code}
                      onChange={(e) => setAddress({ ...address, country_code: e.target.value })}
                      className="w-full h-11 rounded-none bg-obsidian-900 border border-obsidian-600 text-bone-100 px-3 font-sans text-base sm:text-sm focus:outline-none focus:border-terracotta"
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Copies" testid="ship-qty">
                    <select
                      value={quantity}
                      onChange={(e) => setQuantity(Number(e.target.value))}
                      className="w-full h-11 rounded-none bg-obsidian-900 border border-obsidian-600 text-bone-100 px-3 font-sans text-base sm:text-sm focus:outline-none focus:border-terracotta"
                    >
                      {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </Field>
                </div>
              </section>
            )}

            {/* Estimate + Order CTA */}
            {unlocked && (
              <section className="mb-10 border border-obsidian-600 bg-obsidian-800 p-5 sm:p-6" data-testid="keepsake-estimate-panel">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="h-5 w-5 text-terracotta" />
                  <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-bone-500">The price at the press</p>
                </div>
                {!addressComplete ? (
                  <p className="font-serif italic text-bone-400">
                    Complete the shipping address to see live pricing from Lulu.
                  </p>
                ) : estimateLoading ? (
                  <div className="flex items-center gap-3">
                    <Loader2 className="h-4 w-4 animate-spin text-terracotta" />
                    <p className="font-sans text-bone-400">Asking the press for a price…</p>
                  </div>
                ) : estimateError ? (
                  <div className="flex items-start gap-2 text-red-300">
                    <CircleAlert className="h-4 w-4 mt-0.5" />
                    <div>
                      <p className="font-sans text-sm">Lulu could not price this shipment.</p>
                      <p className="font-sans text-xs text-bone-500 mt-1">{String(estimateError).slice(0, 300)}</p>
                    </div>
                  </div>
                ) : estimate ? (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                      <Metric label="Format" value={estimate.product?.label?.split(" · ")[0] || estimate.product?.label} small />
                      <Metric label="Pages" value={estimate.page_count} />
                      <Metric label="Print & ship" value={`$${estimate.lulu_cost_usd.toFixed(2)}`} />
                      <Metric label="Total" value={`$${estimate.total_usd.toFixed(2)}`} accent />
                    </div>
                    <p className="font-sans text-xs text-bone-500 mb-4">
                      {estimate.break_even ? (
                        <>
                          Lulu print + shipping ${estimate.lulu_cost_usd.toFixed(2)} · Umbra binding fee{" "}
                          <span className="text-terracotta line-through">${MARKUP_USD_LABEL}</span>{" "}
                          <span className="text-emerald-400 uppercase tracking-[0.16em] text-[0.6rem]">Attending · at cost</span>
                        </>
                      ) : (
                        <>
                          Lulu print + shipping ${estimate.lulu_cost_usd.toFixed(2)} · Umbra binding fee ${estimate.markup_usd.toFixed(2)}
                        </>
                      )}
                      {estimate.foil_upgrade_usd > 0 && <> · Gold foil ${estimate.foil_upgrade_usd.toFixed(2)}</>}
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        onClick={handleOrder}
                        disabled={ordering}
                        data-testid="keepsake-order-btn"
                        className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-6 py-4 min-h-[48px]"
                      >
                        {ordering ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Package className="h-4 w-4 mr-2" />
                        )}
                        Bind it — ${estimate.total_usd.toFixed(2)}
                      </Button>
                      <Button
                        onClick={handleDownloadPdfPreview}
                        disabled={!canDownloadPreview}
                        data-testid="keepsake-download-btn"
                        variant="outline"
                        className="rounded-none border-obsidian-500 text-bone-300 hover:bg-obsidian-800 font-sans uppercase tracking-wider text-xs px-5 py-4 min-h-[48px]"
                      >
                        <Download className="h-4 w-4 mr-2" /> Preview PDF
                      </Button>
                    </div>
                  </>
                ) : null}
              </section>
            )}

            {/* Order history */}
            {orders.length > 0 && (
              <section className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <BookMarked className="h-5 w-5 text-terracotta" />
                  <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-bone-500">Your bound editions</p>
                </div>
                <div className="space-y-3" data-testid="keepsake-orders-list">
                  {orders.map((o) => {
                    const chip = statusChip(o.lulu_status);
                    return (
                      <div
                        key={o.id}
                        className="border border-obsidian-600 bg-obsidian-800 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3"
                        data-testid={`keepsake-order-${o.id}`}
                      >
                        <div className="flex-1">
                          <p className="font-serif text-bone-100 text-lg leading-tight">
                            {(products.find((p) => p.id === o.product_id) || {}).label || o.product_id}
                          </p>
                          <p className="font-sans text-xs text-bone-500 mt-1">
                            {o.quantity} copy · {o.page_count} pp · ${o.total_usd.toFixed(2)} · ships to {o.shipping_address?.city}
                          </p>
                          {o.tracking_urls?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {o.tracking_urls.map((u, i) => (
                                <a key={i} href={u} target="_blank" rel="noreferrer"
                                   className="font-sans text-xs uppercase tracking-[0.18em] text-terracotta hover:underline">
                                  Track parcel
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                        <StatusChip {...chip} paid={o.payment_status === "paid"} />
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Manuscript preview */}
            <section className="space-y-10">
              <div className="flex items-center justify-between">
                <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-bone-500">Manuscript preview</p>
                <button
                  onClick={loadAll}
                  className="inline-flex items-center gap-1.5 font-sans uppercase tracking-[0.18em] text-[0.65rem] text-bone-400 hover:text-terracotta"
                  data-testid="keepsake-refresh"
                >
                  <RefreshCw className="h-3 w-3" /> Refresh
                </button>
              </div>
              {(manuscript.sections || []).map((s) => (
                <div key={s.section_id} data-testid={`keepsake-section-${s.section_id}`}>
                  <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-2">
                    {s.subtitle || "Chapter"}
                  </p>
                  <h2 className="font-serif text-2xl sm:text-3xl text-bone-100 mb-6">{s.title}</h2>
                  <div className="space-y-6">
                    {s.entries.map((e) => (
                      <div key={e.id} className="border-l-2 border-obsidian-600 pl-5">
                        <p className="font-serif italic text-terracotta text-base leading-relaxed mb-2">
                          {e.prompt_text}
                        </p>
                        <p className="font-serif text-bone-200 leading-relaxed mb-3">{e.answer}</p>
                        {e.reflection && (
                          <div className="border-l-2 border-terracotta/40 pl-4 mb-3">
                            <p className="font-sans uppercase tracking-[0.22em] text-[0.55rem] text-terracotta mb-1">The Mirror</p>
                            <p className="font-serif italic text-bone-300 leading-relaxed">{e.reflection}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {manuscript.totals.entries === 0 && (
                <div className="border border-obsidian-600 p-10 text-center">
                  <p className="font-serif italic text-bone-300 mb-2">The pages are still blank.</p>
                  <p className="font-sans text-sm text-bone-500">
                    Save your first entry to begin gathering the manuscript.
                  </p>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, accent, small }) {
  return (
    <div>
      <p className="font-sans uppercase tracking-[0.2em] text-[0.55rem] text-bone-500 mb-1">{label}</p>
      <p className={`font-serif ${accent ? "text-terracotta text-3xl" : small ? "text-bone-100 text-base" : "text-bone-100 text-2xl"}`}>
        {value}
      </p>
    </div>
  );
}

function Field({ label, testid, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="font-sans uppercase tracking-[0.2em] text-[0.55rem] text-bone-500">{label}</label>
      <div className="mt-1" data-testid={testid}>{children}</div>
    </div>
  );
}

function StatusChip({ label, tone, paid }) {
  const styles = {
    ok: "border-terracotta text-terracotta",
    warn: "border-amber-500/60 text-amber-400",
    err: "border-red-400/60 text-red-300",
    muted: "border-obsidian-500 text-bone-400",
  };
  return (
    <span
      className={`inline-flex items-center gap-2 border px-3 py-2 font-sans uppercase tracking-[0.18em] text-[0.6rem] ${styles[tone] || styles.muted}`}
    >
      {tone === "ok" ? <CircleCheck className="h-3.5 w-3.5" /> : tone === "err" ? <CircleAlert className="h-3.5 w-3.5" /> : null}
      {paid && tone === "muted" ? "Paid · queued" : label}
    </span>
  );
}

function ScopeCard({ active, label, tagline, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-4 sm:p-5 border transition-colors min-h-[92px] ${
        active
          ? "border-terracotta bg-obsidian-800"
          : "border-obsidian-600 bg-obsidian-900 hover:border-terracotta/60"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-1">
        <p className="font-serif text-bone-100 text-lg leading-tight">{label}</p>
        {active && <Check className="h-4 w-4 text-terracotta flex-shrink-0 mt-1" />}
      </div>
      <p className="font-sans text-xs text-bone-500 leading-relaxed">{tagline}</p>
    </button>
  );
}

function ToggleRow({ label, hint, active, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer" data-testid={testid}>
      <input
        type="checkbox"
        checked={active}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 accent-terracotta cursor-pointer"
      />
      <span className="flex-1">
        <span className="block font-serif text-bone-100 text-base leading-tight">{label}</span>
        <span className="block font-sans text-xs text-bone-500 mt-0.5">{hint}</span>
      </span>
    </label>
  );
}
